/**
 * Lexicon data for the convergence rule pack.
 *
 * SOURCE: "Search x Chat Convergence - MVP Triggering Architecture", canonical
 * all-in-one rule catalog and `STOP_WORDS_V1`.
 * https://hello.atlassian.net/wiki/spaces/jirasearch/pages/7525895377
 *
 * Every list here is **exact-match pack data**, never a heuristic: a cue matches
 * only as a whole word or whole phrase at the position its rule specifies. Adding,
 * removing, or reordering a member requires a new pack version and regenerated
 * corpus evidence, which is why these arrays are frozen and typed `readonly`.
 *
 * Multi-word cues are listed longest-first within their family so a longest-match
 * strip cannot be pre-empted by a shorter sibling (`find me` before `find`,
 * `search for` before `search`).
 */

const freeze = <T extends string>(values: readonly T[]): readonly T[] => Object.freeze([...values]);

/**
 * Words removed from a derived operand span before asking whether any content
 * remains.
 *
 * Filtering is case-folded and applies **only** to derived operand spans outside
 * protected ranges. These words are never deleted from `qMatch` or `wordTokens`,
 * so token counts and rule positions are unaffected.
 */
export const STOP_WORDS_V1: readonly string[] = freeze([
	'a',
	'an',
	'the',
	'this',
	'that',
	'these',
	'those',
	'i',
	'me',
	'my',
	'mine',
	'we',
	'us',
	'our',
	'ours',
	'you',
	'your',
	'yours',
	'it',
	'its',
	'they',
	'them',
	'their',
	'theirs',
	'something',
	'anything',
	'am',
	'is',
	'are',
	'was',
	'were',
	'be',
	'been',
	'being',
	'do',
	'does',
	'did',
	'can',
	'could',
	'may',
	'might',
	'must',
	'shall',
	'should',
	'will',
	'would',
	'and',
	'or',
	'but',
	'to',
	'for',
	'of',
	'in',
	'on',
	'at',
	'by',
	'from',
	'with',
	'as',
	'please',
	'kindly',
]);

/**
 * Connectors stripped from operand spans alongside `STOP_WORDS_V1`.
 *
 * Only the members that are not already stop words are listed; the operand
 * evidence helper removes the union of both sets.
 */
export const CONNECTORS_V1: readonly string[] = freeze(['vs', 'versus']);

/** At most one of these may precede the first semantic cue of a Chat rule. */
export const REQUEST_PREFIXES: readonly string[] = freeze([
	'could you please',
	'would you please',
	'can you please',
	'will you please',
	'could you',
	'would you',
	'can you',
	'will you',
	'please',
	'kindly',
]);

/** Politeness markers permitted before the `S-NAVIGATE` and `S-AMBIGUOUS-OPEN` cues. */
export const POLITENESS_PREFIXES: readonly string[] = freeze(['please', 'kindly']);

/** Coordinators that block a request cue when one immediately follows it. */
export const COORDINATORS: readonly string[] = freeze(['and', 'or']);

/** Exact trailing beneficiary frames that license the framed request variant. */
export const BENEFICIARY_FRAMES: readonly string[] = freeze([
	'for ourselves',
	'for myself',
	'for me',
	'for us',
]);

/** `S-NAVIGATE` cues that need only operand evidence after them. */
export const NAVIGATION_CUES: readonly string[] = freeze([
	'navigate to',
	'take me to',
	'go to',
	'visit',
]);

/** `S-NAVIGATE`'s weaker cue: it wins only with an explicit navigation target. */
export const OPEN_CUE = 'open';

/**
 * Singular navigable artefacts that, with further operand evidence, make `open`
 * an explicit navigation rather than an ambiguous request.
 *
 * Deliberately singular-only and Confluence/Home-shaped: a plural noun reads as a
 * browse intent, which belongs on Search through the ordinary fallback rather than
 * through a navigation lock.
 */
export const NAVIGABLE_ARTEFACTS: readonly string[] = freeze([
	'page',
	'document',
	'doc',
	'blog',
	'blogpost',
	'blog post',
	'space',
	'whiteboard',
	'database',
	'folder',
	'attachment',
	'comment',
	'template',
	'goal',
	'project',
	'board',
	'dashboard',
	'report',
]);

/** `C-ACTION` cues (priority 960). */
export const ACTION_CUES: readonly string[] = freeze([
	'comment on',
	'create',
	'publish',
	'edit',
	'update',
	'rename',
	'move',
	'copy',
	'archive',
	'delete',
	'restore',
	'share',
	'restrict',
]);

/** `C-EXPLAIN` cues (priority 955). */
export const EXPLAIN_CUES: readonly string[] = freeze([
	'help me understand',
	'walk me through',
	'elaborate on',
	'explain',
	'describe',
	'define',
]);

/** `C-RETRIEVE` cues (priority 950). */
export const RETRIEVE_CUES: readonly string[] = freeze([
	'search for',
	'look up',
	'find me',
	'show me',
	'tell me',
	'give me',
	'retrieve',
	'find',
	'show',
	'list',
]);

/**
 * Spans that keep a `find` query on the residual path.
 *
 * `find out why|what|how` is an indirect question rather than a retrieval
 * request, so the catalog excludes it explicitly.
 */
export const RETRIEVE_EXCLUSIONS: readonly string[] = freeze([
	'find out why',
	'find out what',
	'find out how',
]);

/** `C-TRANSFORM` cues taking exactly one complete operand (priority 945). */
export const TRANSFORM_SINGLE_OPERAND_CUES: readonly string[] = freeze([
	'summarize',
	'summarise',
	'rewrite',
	'extract',
	'draft',
	'translate',
]);

/** `C-TRANSFORM`'s two-operand cue. */
export const TRANSFORM_COMPARE_CUE = 'compare';

/** The only separators permitted between `compare`'s two operands. */
export const COMPARE_SEPARATORS: readonly string[] = freeze(['versus', 'with', 'and', 'vs']);

/** `C-REQUEST` and `C-REQUEST-BARE` share this verb set (priorities 943 and 942). */
export const REQUEST_VERBS: readonly string[] = freeze([
	'troubleshoot',
	'investigate',
	'recommend',
	'refactor',
	'optimise',
	'optimize',
	'generate',
	'evaluate',
	'validate',
	'diagnose',
	'organise',
	'organize',
	'prepare',
	'provide',
	'suggest',
	'analyse',
	'analyze',
	'improve',
	'verify',
	'assess',
	'review',
	'debug',
	'check',
	'build',
	'write',
	'make',
	'plan',
	'fix',
]);

/** `C-WH-QUESTION` token-0 words (priority 930). */
export const WH_WORDS: readonly string[] = freeze([
	'where',
	'what',
	'how',
	'when',
	'who',
	'which',
	'why',
	'whose',
]);

/**
 * Contracted wh-forms accepted at token 0.
 *
 * The catalog admits the ASCII apostrophe, the curly apostrophe, and the
 * apostrophe-free spelling. `which` and `whose` have no contracted form, so they
 * are absent by design rather than by omission.
 */
export const WH_CONTRACTION_STEMS: readonly string[] = freeze([
	'what',
	'where',
	'who',
	'how',
	'when',
	'why',
]);

/** `C-AUX-QUESTION` token-0 auxiliaries (priority 925). */
export const AUX_WORDS: readonly string[] = freeze([
	'is',
	'are',
	'was',
	'were',
	'do',
	'does',
	'did',
	'can',
	'could',
	'should',
	'would',
	'will',
	'has',
	'have',
	'had',
]);
